import React, { useState, useEffect, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";

// Multi-select dropdown component
function MultiSelectDropdown({ label, options, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term to avoid filtering on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredOptions = useMemo(() => {
    if (!debouncedSearchTerm) return options;
    return options.filter(opt =>
      opt.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
  }, [options, debouncedSearchTerm]);

  const handleToggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleClear = () => {
    onChange([]);
    setIsOpen(false);
  };

  const displayText = selected.length === 0
    ? "(All)"
    : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  return (
    <div className="sar-filter-dropdown">
      <label className="sar-filter-label">{label}</label>
      <div
        className={`cug-dropdown-trigger ${isOpen ? "cug-dropdown-trigger-open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="cug-dropdown-placeholder">{displayText}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {isOpen && (
        <>
          <div className="cug-dropdown-backdrop" onClick={() => setIsOpen(false)} />
          <div className="cug-dropdown-panel" onClick={(e) => e.stopPropagation()}>
            <div className="cug-dropdown-search-wrap">
              <input
                type="text"
                className="cug-dropdown-search"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="cug-dropdown-list">
              {filteredOptions.length === 0 ? (
                <div className="cug-dropdown-empty">No matches</div>
              ) : (
                filteredOptions.map((opt) => (
                  <div
                    key={opt}
                    className={`cug-dropdown-item ${selected.includes(opt) ? "cug-dropdown-item-selected" : ""}`}
                    onClick={() => handleToggle(opt)}
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(opt)}
                      onChange={() => { }}
                      className="cug-dropdown-checkbox"
                    />
                    <span>{opt}</span>
                  </div>
                ))
              )}
            </div>
            {selected.length > 0 && (
              <div className="cug-dropdown-footer">
                <button className="cug-dropdown-clear" onClick={handleClear}>
                  Clear all
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Memoize component to prevent unnecessary re-renders
const MemoizedMultiSelectDropdown = React.memo(MultiSelectDropdown);

function SecurityAccessReport() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    Application: [],
    Entitlement: [],
    EmplCorpo: [],
    EmplCountry: [],
    EmplRegion: [],
    EmplFacility: [],
    EmployeeName: [],
    SupvName: [],
    CPEH: [],
    RelationshipTitle: [],
    Koch_BusinessUnit: [],
    Division: []
  });

  // Search and pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState("EmployeeName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const rowsPerPage = 50;

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Step 1: Get pre-signed URL from API
        const apiResponse = await fetch(`${API_BASE}/security-access-report`);
        const apiData = await apiResponse.json();

        if (!apiResponse.ok) {
          throw new Error(apiData.error || `HTTP ${apiResponse.status}`);
        }

        if (!apiData.downloadUrl) {
          throw new Error("No download URL provided by API");
        }

        setLastUpdated(apiData.lastUpdated);

        // Step 2: Fetch actual data from pre-signed S3 URL
        const dataResponse = await fetch(apiData.downloadUrl);
        const reportData = await dataResponse.json();

        if (!dataResponse.ok) {
          throw new Error(`Failed to download report data: HTTP ${dataResponse.status}`);
        }

        setRawData(reportData.records || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Extract unique values for each filter
  const filterOptions = useMemo(() => {
    const options = {};
    Object.keys(filters).forEach(key => {
      if (key === 'EmployeeName') {
        // For EmployeeName, format as "EmployeeID-EmployeeName" to match Tableau
        const uniqueValues = [...new Set(rawData.map(row => 
          row.EmployeeID && row.EmployeeName ? `${row.EmployeeID}-${row.EmployeeName}` : null
        ).filter(Boolean))].sort();
        options[key] = uniqueValues;
      } else {
        const uniqueValues = [...new Set(rawData.map(row => row[key]).filter(Boolean))].sort();
        options[key] = uniqueValues;
      }
    });
    return options;
  }, [rawData]);

  // Filter and search data
  const filteredData = useMemo(() => {
    let result = rawData;

    // Apply filters
    Object.entries(filters).forEach(([key, selectedValues]) => {
      if (selectedValues.length > 0) {
        if (key === 'EmployeeName') {
          // For EmployeeName filter, match against "EmployeeID-EmployeeName" format
          result = result.filter(row => {
            const combinedValue = row.EmployeeID && row.EmployeeName ? `${row.EmployeeID}-${row.EmployeeName}` : null;
            return selectedValues.includes(combinedValue);
          });
        } else {
          result = result.filter(row => selectedValues.includes(row[key]));
        }
      }
    });

    // Apply persona filter from chart click
    if (selectedPersona) {
      result = result.filter(row => row.Entitlement === selectedPersona);
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(row =>
        Object.values(row).some(val =>
          String(val).toLowerCase().includes(query)
        )
      );
    }

    return result;
  }, [rawData, filters, searchQuery, selectedPersona]);

  // Calculate persona counts for chart
  const personaCounts = useMemo(() => {
    const counts = {};
    filteredData.forEach(row => {
      const persona = row.Entitlement || "Unknown";
      counts[persona] = (counts[persona] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});
  }, [filteredData]);

  // Sort data
  const sortedData = useMemo(() => {
    const sorted = [...filteredData];
    sorted.sort((a, b) => {
      const aVal = String(a[sortColumn] || "");
      const bVal = String(b[sortColumn] || "");
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === "asc" ? comparison : -comparison;
    });
    return sorted;
  }, [filteredData, sortColumn, sortDirection]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, currentPage]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // Chart configuration
  const chartData = {
    labels: Object.keys(personaCounts),
    datasets: [{
      label: "Users Count",
      data: Object.values(personaCounts),
      backgroundColor: ["#f2943e", "#5687b8", "#e95c66", "#6bb6a6", "#a0c566", "#f9ca51"],
      borderWidth: 0,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const persona = Object.keys(personaCounts)[index];
        setSelectedPersona(selectedPersona === persona ? null : persona);
        setCurrentPage(1);
      }
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Users Count by Persona",
        color: "#ffffff",
        font: { size: 16, weight: "600" }
      },
      datalabels: {
        anchor: "end",
        align: "top",
        color: "#ffffff",
        font: { weight: "bold", size: 12 },
        formatter: (value) => value
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: "#b8dced" },
        grid: { color: "rgba(184, 220, 237, 0.1)" }
      },
      x: {
        ticks: { color: "#b8dced" },
        grid: { display: false }
      }
    }
  };

  // Export to CSV
  const handleExport = () => {
    // Define display headers (for CSV header row) and their corresponding data field names
    const columns = [
      { header: "EmployeeID", field: "EmployeeID" },
      { header: "EmployeeName", field: "EmployeeName" },
      { header: "EmployeeEmail", field: "EmployeeEmail" },
      { header: "Application", field: "Application" },
      { header: "Entitlement", field: "Entitlement" },
      { header: "EntitlementDescription", field: "EntitlementDescription" },
      { header: "RelToOrg", field: "RelToOrg" },
      { header: "Relationship Status", field: "RelationshipStatus" },
      { header: "Title", field: "RelationshipTitle" },
      { header: "ExecutiveRestrict", field: "ExecutiveHRRestricted" },
      { header: "HRRestrict", field: "HRRestrict" },
      { header: "SupvEmail", field: "SupvEmail" },
      { header: "SupvName", field: "SupvName" },
      { header: "EmplCountry", field: "EmplCountry" },
      { header: "EmplRegion", field: "EmplRegion" },
      { header: "EmplFacility", field: "EmplFacility" },
      { header: "EmplCorporateEntity", field: "EmplCorpo" },
      { header: "Koch Business Unit", field: "Koch_BusinessUnit" },
      { header: "Division", field: "Division" },
      { header: "Comp Accessible", field: "Comp_AccessPillar" },
      { header: "PII Accessible", field: "Accessibility" },
      { header: "Executive Accessible", field: "Executive_HR_Access" },
      { header: "HR Accessible", field: "HR_Accessible" },
      { header: "CPEH", field: "CPEH" }
    ];
    
    const escape = (val) => `"${String(val || "").replace(/"/g, '""')}"`;
    const rows = sortedData.map(row =>
      columns.map(col => escape(row[col.field])).join(",")
    );
    const csv = [
      `"myHR Security Access Report - Exported ${new Date().toLocaleString()}"`,
      columns.map(col => escape(col.header)).join(","),
      ...rows
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `security-access-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  // Handle filter change
  const handleFilterChange = (filterKey, values) => {
    setFilters(prev => ({ ...prev, [filterKey]: values }));
    setCurrentPage(1);
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setFilters({
      Application: [],
      Entitlement: [],
      EmplCorpo: [],
      EmplCountry: [],
      EmplRegion: [],
      EmplFacility: [],
      EmployeeName: [],
      SupvName: [],
      CPEH: [],
      RelationshipTitle: [],
      Koch_BusinessUnit: [],
      Division: []
    });
    setSelectedPersona(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  // Calculate distinct user count
  const distinctUserCount = useMemo(() => {
    return new Set(filteredData.map(row => row.EmployeeID)).size;
  }, [filteredData]);

  if (loading) {
    return (
      <div className="sar-container">
        <div className="sar-loading">
          <span className="spinner" />
          Loading report data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sar-container">
        <div className="sar-error">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          Error loading report: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="sar-container">
      {/* Header */}
      <header className="cug-header">
        <div className="cug-header-top">
          <div className="cug-title">Security Access Report</div>
        </div>
        <div className="cug-subtitle">
          myHR Security Access{lastUpdated && (
            <span> • Updated {new Date(lastUpdated).toLocaleString("en-US", {
              timeZone: "America/Chicago",
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            })}</span>
          )}
        </div>
      </header>

      <div className="sar-body">
        <div className="sar-body-inner">
          <div className="sar-actions">
            <button className="compare-btn" onClick={handleExport}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export to CSV
            </button>
          </div>

          {/* Filters */}
          <div className="sar-filters">
            <div className="sar-filters-header">
              <div className="sar-filters-title">Filter by</div>
              <button className="sar-clear-all-btn" onClick={handleClearAllFilters}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M3 6h18M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2m3 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6h14zM10 11v6M14 11v6"/>
                </svg>
                Clear all filters
              </button>
            </div>
            <div className="sar-filters-grid">
              <MemoizedMultiSelectDropdown
                label="Application"
                options={filterOptions.Application}
                selected={filters.Application}
                onChange={(val) => handleFilterChange("Application", val)}
              />
              <MemoizedMultiSelectDropdown
                label="Entitlement"
                options={filterOptions.Entitlement}
                selected={filters.Entitlement}
                onChange={(val) => handleFilterChange("Entitlement", val)}
              />
              <MemoizedMultiSelectDropdown
                label="Employee Corp"
                options={filterOptions.EmplCorpo}
                selected={filters.EmplCorpo}
                onChange={(val) => handleFilterChange("EmplCorpo", val)}
              />
              <MemoizedMultiSelectDropdown
                label="Country"
                options={filterOptions.EmplCountry}
                selected={filters.EmplCountry}
                onChange={(val) => handleFilterChange("EmplCountry", val)}
              />
              <MemoizedMultiSelectDropdown
                label="Region"
                options={filterOptions.EmplRegion}
                selected={filters.EmplRegion}
                onChange={(val) => handleFilterChange("EmplRegion", val)}
              />
              <MemoizedMultiSelectDropdown
                label="Facility"
                options={filterOptions.EmplFacility}
                selected={filters.EmplFacility}
                onChange={(val) => handleFilterChange("EmplFacility", val)}
              />
              <MemoizedMultiSelectDropdown
                label="Employee"
                options={filterOptions.EmployeeName}
                selected={filters.EmployeeName}
                onChange={(val) => handleFilterChange("EmployeeName", val)}
              />
              <MemoizedMultiSelectDropdown
                label="Supervisor name"
                options={filterOptions.SupvName}
                selected={filters.SupvName}
                onChange={(val) => handleFilterChange("SupvName", val)}
              />
              <MemoizedMultiSelectDropdown
                label="CPEH"
                options={filterOptions.CPEH}
                selected={filters.CPEH}
                onChange={(val) => handleFilterChange("CPEH", val)}
              />
              <MemoizedMultiSelectDropdown
                label="Job title"
                options={filterOptions.RelationshipTitle}
                selected={filters.RelationshipTitle}
                onChange={(val) => handleFilterChange("RelationshipTitle", val)}
              />
              <MemoizedMultiSelectDropdown
                label="Business unit"
                options={filterOptions.Koch_BusinessUnit}
                selected={filters.Koch_BusinessUnit}
                onChange={(val) => handleFilterChange("Koch_BusinessUnit", val)}
              />
              <MemoizedMultiSelectDropdown
                label="Division"
                options={filterOptions.Division}
                selected={filters.Division}
                onChange={(val) => handleFilterChange("Division", val)}
              />
            </div>
          </div>

      {/* Chart */}
      <div className="sar-chart-card">
        <div className="sar-chart-wrapper">
          <Bar data={chartData} options={chartOptions} />
        </div>
        {selectedPersona && (
          <div className="sar-chart-filter-badge">
            Filtering by persona: <strong>{selectedPersona}</strong>
            <button onClick={() => setSelectedPersona(null)}>✕</button>
          </div>
        )}
      </div>

      {/* Search and count */}
      <div className="sar-controls">
        <input
          type="text"
          className="search-input"
          placeholder="Search across all fields..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
        />
        <div className="sar-count">
          Showing {paginatedData.length} of {sortedData.length} records ({distinctUserCount} distinct users)
          {sortedData.length !== rawData.length && ` (filtered from ${rawData.length} total)`}
        </div>
      </div>

      {/* Table */}
      <div className="sar-table-wrapper">
        <table className="sar-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("EmployeeID")}>
                EmployeeID
                {sortColumn === "EmployeeID" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("EmployeeName")}>
                EmployeeName
                {sortColumn === "EmployeeName" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("EmployeeEmail")}>
                EmployeeEmail
                {sortColumn === "EmployeeEmail" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("Application")}>
                Application
                {sortColumn === "Application" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("Entitlement")}>
                Entitlements
                {sortColumn === "Entitlement" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("EntitlementDescription")}>
                EntitlementDescription
                {sortColumn === "EntitlementDescription" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("RelToOrg")}>
                RelToOrg
                {sortColumn === "RelToOrg" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("RelationshipStatus")}>
                Relationship Status
                {sortColumn === "RelationshipStatus" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("RelationshipTitle")}>
                Title
                {sortColumn === "RelationshipTitle" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("ExecutiveHRRestricted")}>
                ExecutiveRestrict
                {sortColumn === "ExecutiveHRRestricted" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("HRRestrict")}>
                HRRestrict
                {sortColumn === "HRRestrict" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("SupvEmail")}>
                SupvEmail
                {sortColumn === "SupvEmail" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("SupvName")}>
                SupvName
                {sortColumn === "SupvName" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("EmplCountry")}>
                EmplCountry
                {sortColumn === "EmplCountry" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("EmplRegion")}>
                EmplRegion
                {sortColumn === "EmplRegion" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("EmplFacility")}>
                EmplFacility
                {sortColumn === "EmplFacility" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("EmplCorpo")}>
                EmplCorporateEntity
                {sortColumn === "EmplCorpo" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("Koch_BusinessUnit")}>
                Koch Business Unit
                {sortColumn === "Koch_BusinessUnit" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("Division")}>
                Division
                {sortColumn === "Division" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("Comp_AccessPillar")}>
                Comp Accessible
                {sortColumn === "Comp_AccessPillar" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("Accessibility")}>
                PII Accessible
                {sortColumn === "Accessibility" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("Executive_HR_Access")}>
                Executive Accessible
                {sortColumn === "Executive_HR_Access" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("HR_Accessible")}>
                HR Accessible
                {sortColumn === "HR_Accessible" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
              <th onClick={() => handleSort("CPEH")}>
                CPEH
                {sortColumn === "CPEH" && (
                  <span className="sar-sort-icon">{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, idx) => (
              <tr key={idx}>
                <td>{row.EmployeeID}</td>
                <td>{row.EmployeeName}</td>
                <td>{row.EmployeeEmail}</td>
                <td>{row.Application}</td>
                <td>{row.Entitlement}</td>
                <td className="sar-desc-cell">{row.EntitlementDescription}</td>
                <td>{row.RelToOrg}</td>
                <td>{row.RelationshipStatus}</td>
                <td>{row.RelationshipTitle}</td>
                <td>{row.ExecutiveHRRestricted}</td>
                <td>{row.HRRestrict}</td>
                <td>{row.SupvEmail}</td>
                <td>{row.SupvName}</td>
                <td>{row.EmplCountry}</td>
                <td>{row.EmplRegion}</td>
                <td>{row.EmplFacility}</td>
                <td>{row.EmplCorpo}</td>
                <td>{row.Koch_BusinessUnit}</td>
                <td>{row.Division}</td>
                <td>{row.Comp_AccessPillar}</td>
                <td>{row.Accessibility}</td>
                <td>{row.Executive_HR_Access}</td>
                <td>{row.HR_Accessible}</td>
                <td>{row.CPEH}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="sar-pagination">
          <button
            className="sar-page-btn"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="sar-page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="sar-page-btn"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}

export default SecurityAccessReport;
